// Which of kore's five SaveBar states a form is in.
//
// The bar owns the presentation — the pill, the dot, ⌘S, the disabled rules —
// and asks the page for one thing: its state. That derivation is the same
// wherever a page is one form with one Save, so it lives here rather than
// three times over:
//
//   saving  beats everything, so the spinner is never masked by a stale outcome
//   error   beats dirty, so a failure is not hidden by the edit that follows it
//   dirty   beats saved, so editing after a save re-arms Save
//   saved   holds until that next edit — a save that leaves nothing on screen
//           reads as a save that did not happen
//
// The outcome is set by the submit handler, not watched from a mutation:
// `isPending` alone cannot tell "not saved yet" from "saved a moment ago".
//
// Failures come in two kinds and do not behave alike. One the server sent
// stands until the next attempt — nothing the reader types makes it untrue. One
// raised by the fields belongs to the fields: the moment they are clean again,
// a bar still naming them is lying, so it goes.

import type { SaveBarState } from "@pej/kore/components";
import { useState } from "react";

/**
 * Takes kore's SaveBar out of its default `sticky bottom-4` and puts it back
 * in the page flow, at the foot of the form.
 *
 * Pinned to the viewport, the pill hovers over whatever is behind it — it sat
 * across the Tuning fields mid-scroll, and across the template editor's CSS
 * lines. The editor's own hand-rolled bar had already made this call once, for
 * the same reason, before kore's replaced it. And after the density pass these
 * forms are short enough that the foot of the form is rarely far away.
 *
 * One class, because `cn` is tailwind-merge: `static` REPLACES `sticky` rather
 * than stacking with it, and `bottom-4` / `z-40` are inert once it does.
 */
export const SAVE_BAR_IN_FLOW = "static";

export interface SaveBarBinding {
    state: SaveBarState;
    /** The failure to show in the bar; undefined unless `state` is "error". */
    errorMessage: string | undefined;
    /** Call as a submit begins — clears the previous outcome. */
    submitting: () => void;
    saved: () => void;
    /** The save reached the server and it said no. Stands until the next attempt. */
    failed: (message: string) => void;
    /** The save never left the page — a field is wrong. Clears with the field. */
    blocked: (message: string) => void;
}

type Outcome = { ok: true } | { ok: false; message: string; fromFields: boolean };

export interface UseSaveBarProps {
    /** The form has edits not yet sent. */
    dirty: boolean;
    /** A save is in flight. */
    saving: boolean;
    /** The form currently has field errors. Only consulted to decide whether a
     *  `blocked` failure is still true. */
    invalid?: boolean;
}

export function useSaveBar({ dirty, saving, invalid = false }: UseSaveBarProps): SaveBarBinding {
    const [outcome, setOutcome] = useState<Outcome | null>(null);
    const failure = outcome !== null && !outcome.ok ? outcome : null;
    const showFailure = failure !== null && (!failure.fromFields || invalid);

    const state: SaveBarState = saving
        ? "saving"
        : showFailure
          ? "error"
          : dirty
            ? "dirty"
            : outcome?.ok
              ? "saved"
              : "clean";

    return {
        state,
        errorMessage: showFailure ? failure.message : undefined,
        submitting: () => setOutcome(null),
        saved: () => setOutcome({ ok: true }),
        failed: (message: string) => setOutcome({ ok: false, message, fromFields: false }),
        blocked: (message: string) => setOutcome({ ok: false, message, fromFields: true }),
    };
}
